import React, { useEffect, useRef, useState } from "react";
import { Card } from "primereact/card";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";

import { apiFetch } from "../../Helper/Api";
import { t } from "../../Helper/i18n";
import Header from "../../components/Header/Header";
import { Modal, Confirm } from "../../components/UI/Overlays";
import { EmptyState, TableWait, Chip, Alert } from "../../components/UI/Feedback";
import { Segmented } from "../../components/UI/Controls";

const properties = require("../../properties.json");

const EMPTY = { name: "", specialty: "", bio: "", imageUrl: "", enabled: true };

/** Solo coach or a team, and the trainers a team shows on its site and filters the library by. */
export default function Instructors() {
  const toast = useRef(null);
  const [rows, setRows] = useState(null);
  const [mode, setMode] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [doomed, setDoomed] = useState(null);

  const load = () =>
    apiFetch("/instructors")
      .then(setRows)
      .catch(() => setRows([]));

  useEffect(() => {
    document.title = properties.site_name + " | " + t("gym.trainers");
    load();
    apiFetch("/teachers/me")
      .then((me) => setMode(me.teamMode || "SOLO"))
      .catch(() => setMode("SOLO"));
  }, []);

  const say = (severity, detail) => toast.current && toast.current.show({ severity, summary: severity === "error" ? t("common.alert") : t("common.done"), detail });
  const fail = (e) => say("error", (e && e.message) || t("common.failed"));
  const set = (patch) => setForm({ ...form, ...patch });

  const changeMode = (m) =>
    apiFetch("/instructors/team-mode", { method: "PUT", body: { mode: m } })
      .then(() => {
        setMode(m);
        return load();
      })
      .catch(fail);

  const save = () => {
    if (!form.name.trim()) {
      say("error", t("gym.trainers.nameRequired"));
      return;
    }
    setSaving(true);
    apiFetch(form.id ? `/instructors/${form.id}` : "/instructors", {
      method: form.id ? "PUT" : "POST",
      body: { name: form.name, specialty: form.specialty, bio: form.bio, imageUrl: form.imageUrl, enabled: form.enabled },
    })
      .then(() => {
        setForm(null);
        say("success", t("gym.trainers.saved"));
        return load();
      })
      .catch(fail)
      .finally(() => setSaving(false));
  };

  const remove = (i) =>
    apiFetch(`/instructors/${i.id}`, { method: "DELETE" })
      .then(() => {
        setDoomed(null);
        return load();
      })
      .catch((e) => {
        setDoomed(null);
        fail(e);
      });

  const team = mode === "TEAM";

  return (
    <>
      <Header pageName={t("gym.trainers")} action={team ? { label: t("gym.trainers.new"), icon: "pi pi-plus", onClick: () => setForm({ ...EMPTY }) } : undefined} />
      <Toast ref={toast} />
      <Confirm visible={!!doomed} onHide={() => setDoomed(null)} name={doomed ? doomed.name : ""} note={t("gym.trainers.deleteNote")} onConfirm={() => remove(doomed)} />
      <Modal
        visible={!!form}
        onHide={() => setForm(null)}
        title={form && form.id ? t("gym.trainers.edit") : t("gym.trainers.new")}
        footer={
          <button type="button" className="ui-btn" disabled={saving} onClick={save}>
            {saving ? <i className="pi pi-spin pi-spinner"></i> : <i className="pi pi-check"></i>} {t("common.save")}
          </button>
        }
      >
        {form ? (
          <div className="ui-modal-form">
            <label className="ui-filter">
              <span>{t("gym.trainers.name")}</span>
              <InputText value={form.name} maxLength={120} onChange={(e) => set({ name: e.target.value })} />
            </label>
            <label className="ui-filter">
              <span>{t("gym.trainers.specialty")}</span>
              <InputText value={form.specialty || ""} maxLength={160} onChange={(e) => set({ specialty: e.target.value })} />
            </label>
            <label className="ui-filter">
              <span>{t("gym.trainers.bio")}</span>
              <InputTextarea rows={4} value={form.bio || ""} maxLength={1200} onChange={(e) => set({ bio: e.target.value })} />
            </label>
            <label className="ui-filter">
              <span>{t("gym.trainers.image")}</span>
              <InputText value={form.imageUrl || ""} placeholder="https://" onChange={(e) => set({ imageUrl: e.target.value })} />
            </label>
            {form.owner ? null : (
              <label className="ui-filter is-inline">
                <input type="checkbox" checked={form.enabled} onChange={(e) => set({ enabled: e.target.checked })} />
                <span>{t("gym.trainers.enabled")}</span>
              </label>
            )}
          </div>
        ) : null}
      </Modal>

      <div className="content">
        <div className="container">
          <div className="card">
            <Card>
              {mode ? (
                <div className="ui-filter">
                  <span>{t("gym.trainers.mode")}</span>
                  <Segmented
                    value={mode}
                    onChange={changeMode}
                    items={[
                      { key: "SOLO", label: t("gym.trainers.solo") },
                      { key: "TEAM", label: t("gym.trainers.team") },
                    ]}
                  />
                </div>
              ) : null}
              {!team && mode ? (
                <Alert kind="info" title={t("gym.trainers.soloTitle")}>
                  {t("gym.trainers.soloNote")}
                </Alert>
              ) : (
                <DataTable
                  value={rows || []}
                  className="p-datatable-responsive-demo p-datatable-gridlines p-datatable-striped"
                  emptyMessage={rows === null ? <TableWait /> : <EmptyState icon="pi pi-users" title={t("gym.trainers.emptyTitle")} hint={t("gym.trainers.emptyText")} />}
                >
                  <Column field="name" header={t("gym.trainers.name")} />
                  <Column field="specialty" header={t("gym.trainers.specialty")} />
                  <Column header={t("gym.trainers.status")} body={(r) => (r.owner ? <Chip kind="wait">{t("gym.trainers.you")}</Chip> : <Chip kind={r.enabled ? "ok" : "off"}>{r.enabled ? t("gym.plans.on") : t("gym.plans.off")}</Chip>)} />
                  <Column
                    header=""
                    body={(r) => (
                      <div className="p-d-flex">
                        <Button icon="pi pi-pencil" className="p-button-rounded p-button-secondary p-ml-2" onClick={() => setForm({ ...r })} />
                        {r.owner ? null : <Button icon="pi pi-trash" className="p-button-rounded p-button-danger" onClick={() => setDoomed(r)} />}
                      </div>
                    )}
                  />
                </DataTable>
              )}
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}
